import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Briefcase, FlaskConical, Users, Compass, FileText,
    UserCircle, LifeBuoy, Bell, LogOut, Menu, ShieldCheck, Building2, GraduationCap,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "./Header";

const LINKS = {
    student: [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/internships", label: "Internships", icon: Briefcase },
        { to: "/projects", label: "Projects", icon: FlaskConical },
        { to: "/faculty", label: "Faculty", icon: Users },
        { to: "/career-paths", label: "Career Paths", icon: Compass },
        { to: "/applications", label: "My Applications", icon: FileText },
        { to: "/profile", label: "Profile", icon: UserCircle },
        { to: "/support", label: "Support", icon: LifeBuoy },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    faculty: [
        { to: "/faculty-portal", label: "Faculty Portal", icon: GraduationCap },
        { to: "/internships", label: "Internships", icon: Briefcase },
        { to: "/projects", label: "Projects", icon: FlaskConical },
        { to: "/support", label: "Support", icon: LifeBuoy },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    organization: [
        { to: "/org-portal", label: "Organization Portal", icon: Building2 },
        { to: "/internships", label: "Internships", icon: Briefcase },
        { to: "/projects", label: "Projects", icon: FlaskConical },
        { to: "/support", label: "Support", icon: LifeBuoy },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
    admin: [
        { to: "/admin", label: "Admin Portal", icon: ShieldCheck },
        { to: "/internships", label: "Internships", icon: Briefcase },
        { to: "/projects", label: "Projects", icon: FlaskConical },
        { to: "/faculty", label: "Faculty", icon: Users },
        { to: "/support", label: "Support", icon: LifeBuoy },
        { to: "/notifications", label: "Notifications", icon: Bell },
    ],
};

function SidebarLinks({ links, onNavigate }) {
    return (
        <nav className="flex flex-col gap-1">
            {links.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={onNavigate} data-testid={`sidebar-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                            isActive ? "bg-white/10 text-sand" : "text-white/70 hover:bg-white/5 hover:text-white"
                        }`}>
                    <l.icon className="h-4 w-4" />
                    {l.label}
                </NavLink>
            ))}
        </nav>
    );
}

export default function DashboardLayout({ children, title }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const links = LINKS[user?.role] || LINKS.student;

    useEffect(() => {
        api.get("/notifications")
            .then((r) => setUnread(r.data.filter((n) => !n.read).length))
            .catch(() => {});
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <div className="flex min-h-screen bg-background" data-testid="dashboard-layout">
            <aside className="sticky top-0 hidden h-screen w-64 flex-col bg-forest p-5 md:flex">
                <div className="mb-8 px-2"><Logo dark /></div>
                <SidebarLinks links={links} />
                <div className="mt-auto border-t border-white/10 pt-4">
                    <div className="mb-3 px-4">
                        <p className="text-sm font-medium text-white">{user?.name}</p>
                        <p className="text-xs capitalize text-white/50">{user?.role}</p>
                    </div>
                    <button data-testid="logout-button" onClick={handleLogout}
                        className="flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-white/70 transition-colors duration-200 hover:bg-white/5 hover:text-white">
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            </aside>
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="glass sticky top-0 z-40 flex h-16 items-center justify-between px-6">
                    <div className="flex items-center gap-3">
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <Button data-testid="mobile-menu-button" variant="ghost" size="icon" className="md:hidden"><Menu className="h-5 w-5" /></Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-64 bg-forest p-5">
                                <div className="mb-8"><Logo dark /></div>
                                <SidebarLinks links={links} onNavigate={() => setOpen(false)} />
                            </SheetContent>
                        </Sheet>
                        <h1 className="font-display text-lg font-semibold" data-testid="page-title">{title}</h1>
                    </div>
                    <Link to="/notifications" data-testid="notifications-bell" className="relative rounded-full p-2 transition-colors duration-200 hover:bg-muted">
                        <Bell className="h-5 w-5 text-muted-foreground" />
                        {unread > 0 && (
                            <span data-testid="notifications-unread-count" className="absolute -right-0.5 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-accent text-[10px] font-bold text-white">{unread}</span>
                        )}
                    </Link>
                </div>
                <main className="flex-1 p-6 md:p-10">{children}</main>
            </div>
        </div>
    );
}
